import { useState } from "react";
import type { User } from "@/mocks/users";
import type { Department } from "@/lib/api";

interface DepartmentsTabProps {
  users: User[];
  departments: Department[];
  deptNames: string[];
  loading: boolean;
  onAddDepartment: (name: string) => Promise<void>;
  onDeleteDepartment: (id: number) => Promise<void>;
}

export default function DepartmentsTab({ users, departments, deptNames, loading, onAddDepartment, onDeleteDepartment }: DepartmentsTabProps) {
  const [newDept, setNewDept] = useState("");
  const [adding, setAdding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Department | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleAdd = async () => {
    const trimmed = newDept.trim();
    if (!trimmed || deptNames.includes(trimmed)) return;
    setAdding(true);
    await onAddDepartment(trimmed);
    setNewDept("");
    setAdding(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await onDeleteDepartment(deleteTarget.id);
    setDeleteTarget(null);
    setDeleting(false);
  };

  const deptStats = departments.map((dept) => {
    const deptUsers = users.filter((u) => u.department === dept.name);
    const activeCount = deptUsers.filter((u) => u.status === "Active").length;
    return { dept, total: deptUsers.length, active: activeCount };
  });

  return (
    <div>
      <div className="bg-white rounded-lg border border-background-200 p-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <input
              type="text"
              value={newDept}
              onChange={(e) => setNewDept(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              placeholder="Enter new department name..."
              className="w-full h-9 px-3 border border-background-300 rounded-md text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={!newDept.trim() || adding}
            className="h-9 px-4 bg-primary-600 text-white text-sm font-semibold rounded-md hover:bg-primary-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            {adding && <i className="ri-loader-4-line animate-spin text-sm"></i>}
            Add Department
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200 bg-background-100">
          <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
            {departments.length} Departments
          </span>
        </div>
        {loading ? (
          <div className="py-10 text-center text-foreground-400 text-sm">
            <i className="ri-loader-4-line animate-spin mr-2"></i>Loading...
          </div>
        ) : (
          <div className="divide-y divide-background-100">
            {deptStats.map(({ dept, total, active }) => (
              <div key={dept.id} className="flex items-center justify-between px-4 py-3.5 hover:bg-background-50 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
                    <i className="ri-building-2-line text-foreground-500 text-base"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-foreground-800 text-sm font-medium truncate">{dept.name}</p>
                    <p className="text-foreground-400 text-xs">
                      {total} user{total !== 1 ? "s" : ""}
                      <span className="mx-1.5">·</span>
                      <span className="text-secondary-600 font-medium">{active} active</span>
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setDeleteTarget(dept)}
                  disabled={total > 0}
                  className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors cursor-pointer ${
                    total > 0
                      ? "text-foreground-300 cursor-not-allowed"
                      : "text-foreground-400 hover:text-accent-700 hover:bg-accent-50"
                  }`}
                  title={total > 0 ? "Remove all users first" : "Delete department"}
                >
                  <i className="ri-delete-bin-6-line text-sm"></i>
                </button>
              </div>
            ))}
            {deptStats.length === 0 && (
              <div className="px-4 py-12 text-center text-foreground-400 text-sm">No departments configured.</div>
            )}
          </div>
        )}
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative bg-white rounded-lg w-full max-w-[400px] mx-4 p-6">
            <div className="w-10 h-10 rounded-full bg-accent-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-error-warning-line text-accent-600 text-lg"></i>
            </div>
            <h3 className="text-foreground-800 text-base font-semibold text-center mb-1">Delete Department</h3>
            <p className="text-foreground-500 text-sm text-center mb-5">
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This cannot be undone.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => setDeleteTarget(null)} className="h-9 px-4 rounded-md text-sm font-medium text-foreground-600 border border-background-300 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="h-9 px-4 bg-accent-600 text-white text-sm font-semibold rounded-md hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 disabled:opacity-60"
              >
                {deleting && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
